/**
 * Gallery Page Component
 * Initializes Flickity carousel for gallery page with lightbox functionality
 */

import Flickity from 'flickity';
import 'flickity/dist/flickity.min.css';

export default class GalleryPage {
  constructor(element) {
    this.element = element;
    this.mainCarousel = element.querySelector('[data-gallery-carousel]');
    this.lightbox = document.querySelector('[data-gallery-lightbox]');
    this.lightboxCarousel = this.lightbox?.querySelector('[data-lightbox-carousel]');
    
    // Navigation elements
    this.prevBtn = element.querySelector('.gallery-navigation__button--prev');
    this.nextBtn = element.querySelector('.gallery-navigation__button--next');
    this.counter = element.querySelector('.gallery-navigation__current');
    this.total = element.querySelector('.gallery-navigation__total');
    this.caption = element.querySelector('.gallery-navigation__caption');
    
    // Lightbox elements
    this.lightboxClose = this.lightbox?.querySelector('.gallery-lightbox__close');
    this.lightboxOverlay = this.lightbox?.querySelector('.gallery-lightbox__overlay');
    this.lightboxPrevBtn = this.lightbox?.querySelector('.gallery-lightbox__button--prev');
    this.lightboxNextBtn = this.lightbox?.querySelector('.gallery-lightbox__button--next');
    this.lightboxCounter = this.lightbox?.querySelector('.gallery-lightbox__current');
    this.lightboxTotal = this.lightbox?.querySelector('.gallery-lightbox__total');
    this.lightboxCaption = this.lightbox?.querySelector('.gallery-lightbox__caption');
    
    this.mainFlickity = null;
    this.lightboxFlickity = null;
    this.images = [];
    
    this.init();
  }

  init() {
    if (!this.mainCarousel) return;

    // Collect image data
    this.collectImageData();

    // Initialize main carousel
    this.initMainCarousel();

    // Initialize lightbox
    this.initLightbox();
  }

  collectImageData() {
    const slides = this.mainCarousel.querySelectorAll('[data-image-id]');
    this.images = Array.from(slides).map((slide) => ({
      id: slide.getAttribute('data-image-id'),
      caption: slide.getAttribute('data-caption') || '',
    }));
  }

  initMainCarousel() {
    // Initialize Flickity for main carousel
    this.mainFlickity = new Flickity(this.mainCarousel, {
      cellAlign: 'center',
      contain: false, // Allow side slides to overflow
      wrapAround: true,
      prevNextButtons: false,
      pageDots: false,
      adaptiveHeight: false,
      imagesLoaded: true,
      percentPosition: false, // Use pixel positioning for more control
    });

    // Bind custom controls
    if (this.prevBtn) {
      this.prevBtn.addEventListener('click', () => {
        this.mainFlickity.previous();
      });
    }

    if (this.nextBtn) {
      this.nextBtn.addEventListener('click', () => {
        this.mainFlickity.next();
      });
    }

    // Update counter and caption on change
    this.mainFlickity.on('change', (index) => {
      this.updateMainCounter(index);
      this.updateMainCaption(index);
    });

    // Open lightbox on image click (desktop only)
    const slides = this.mainCarousel.querySelectorAll('.gallery-carousel__slide');
    slides.forEach((slide, index) => {
      slide.addEventListener('click', () => {
        // Only open lightbox on desktop (> 768px)
        if (window.innerWidth > 768) {
          this.openLightbox(index);
        }
      });
    });

    // Initialize counter
    this.updateMainCounter(0);
    this.updateMainCaption(0);
  }

  initLightbox() {
    if (!this.lightbox || !this.lightboxCarousel) return;

    // Initialize Flickity for lightbox carousel
    this.lightboxFlickity = new Flickity(this.lightboxCarousel, {
      cellAlign: 'center',
      contain: true,
      wrapAround: true,
      prevNextButtons: false,
      pageDots: false,
      adaptiveHeight: false,
      imagesLoaded: true,
    });

    // Bind lightbox controls
    if (this.lightboxPrevBtn) {
      this.lightboxPrevBtn.addEventListener('click', () => {
        this.lightboxFlickity.previous();
      });
    }

    if (this.lightboxNextBtn) {
      this.lightboxNextBtn.addEventListener('click', () => {
        this.lightboxFlickity.next();
      });
    }

    // Close lightbox
    if (this.lightboxClose) {
      this.lightboxClose.addEventListener('click', () => {
        this.closeLightbox();
      });
    }

    if (this.lightboxOverlay) {
      this.lightboxOverlay.addEventListener('click', () => {
        this.closeLightbox();
      });
    }

    // Close on ESC key
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.lightbox.classList.contains('is-active')) {
        this.closeLightbox();
      }
    });

    // Update lightbox counter and caption on change
    this.lightboxFlickity.on('change', (index) => {
      this.updateLightboxCounter(index);
      this.updateLightboxCaption(index);
    });
  }

  updateMainCounter(index) {
    if (this.counter) {
      this.counter.textContent = index + 1;
    }
  }

  updateMainCaption(index) {
    if (this.caption && this.images[index]) {
      this.caption.textContent = this.images[index].caption;
    }
  }

  updateLightboxCounter(index) {
    if (this.lightboxCounter) {
      this.lightboxCounter.textContent = index + 1;
    }
  }

  updateLightboxCaption(index) {
    if (this.lightboxCaption && this.images[index]) {
      this.lightboxCaption.textContent = this.images[index].caption;
    }
  }

  openLightbox(index) {
    if (!this.lightbox) return;

    // Show lightbox
    this.lightbox.classList.add('is-active');
    document.body.style.overflow = 'hidden';

    // Resize Flickity after lightbox is visible
    if (this.lightboxFlickity) {
      // Wait for CSS transition to complete
      setTimeout(() => {
        this.lightboxFlickity.resize();
        this.lightboxFlickity.select(index, false, true);
        this.updateLightboxCounter(index);
        this.updateLightboxCaption(index);
      }, 50);
    }

    // Sync main carousel with lightbox
    if (this.mainFlickity) {
      this.mainFlickity.select(index, false, true);
    }
  }

  closeLightbox() {
    if (!this.lightbox) return;

    this.lightbox.classList.remove('is-active');
    document.body.style.overflow = '';
  }
}

// Auto-initialize
document.addEventListener('DOMContentLoaded', () => {
  const galleryPage = document.querySelector('.gallery-page');
  if (galleryPage) {
    new GalleryPage(galleryPage);
  }
});
